import {
  ApiError,
  request,
  type ClinicalAttachment,
  type ClinicalUploadIntent,
  type ConsultationInput,
  type CrmConsultation,
  type CrmPatientDetail,
  type CrmMedication,
  type MedicationInput,
} from "./api";

const BASE = `${(import.meta.env.VITE_API_URL as string | undefined) ?? "https://api.tappet.com.ar"}/api/v1`;
const DEVICE_KEY = "tappet-crm-vet-device";
const SESSION_KEY = "tappet-crm-temporary-vet-access";
let inMemoryDeviceId: string | null = null;

export interface TemporaryVeterinarySession {
  token: string;
  expiresAt: string;
  organizationId: string;
  userId: string;
  petPublicSlug: string;
}

export interface VeterinaryPage {
  offset: number;
  limit: number;
  total: number;
  hasMore: boolean;
}

export type VeterinaryHealthRecord = Record<string, unknown> & {
  id?: string;
  name?: string;
  title?: string;
};

export interface TemporaryVeterinaryContext {
  pet: {
    id: string;
    publicSlug: string;
    name: string;
    species: "DOG" | "CAT";
    cityArea: string | null;
  };
  professional: {
    fullName: string;
    licenseNumber: string | null;
    jurisdiction: string | null;
    verified: boolean;
  };
  summary: CrmPatientDetail["medicalSummary"];
  consultations: Array<Omit<CrmConsultation, "canAttach"> & { canAttach?: boolean }>;
  vaccinations: CrmPatientDetail["vaccinations"];
  pagination: { consultations: VeterinaryPage; vaccinations: VeterinaryPage };
  health: {
    patient: VeterinaryHealthRecord;
    reproductive: VeterinaryHealthRecord | null;
    medications: CrmMedication[];
    tutorReportedMedications: VeterinaryHealthRecord[];
    problems: VeterinaryHealthRecord[];
    preventiveCare: VeterinaryHealthRecord[];
  };
}

export interface TemporaryVeterinaryRedeem extends TemporaryVeterinaryContext {
  sessionToken: string;
  expiresAt: string;
}

function deviceId(): string {
  let id: string | null = null;
  try {
    id = sessionStorage.getItem(DEVICE_KEY);
  } catch {
    /* Restricted browser storage: keep this visit in memory. */
  }
  if (!id) {
    id = inMemoryDeviceId ?? crypto.randomUUID();
    try {
      sessionStorage.setItem(DEVICE_KEY, id);
    } catch {
      /* No persistent clinical credential fallback. */
    }
  }
  inMemoryDeviceId = id;
  return id;
}

export function saveTemporaryVeterinarySession(session: TemporaryVeterinarySession): void {
  // Only the opaque, short-lived credential is kept in this tab; never clinical data or the tutor code.
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    /* The current in-memory visit remains usable. */
  }
}

export function clearTemporaryVeterinarySession(): void {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* Nothing persistent can be read in this browser. */
  }
}

export function readTemporaryVeterinarySession(
  userId: string,
  petPublicSlug: string,
): TemporaryVeterinarySession | null {
  try {
    const value = JSON.parse(
      sessionStorage.getItem(SESSION_KEY) ?? "null",
    ) as TemporaryVeterinarySession | null;
    if (
      !value ||
      value.userId !== userId ||
      value.petPublicSlug !== petPublicSlug ||
      typeof value.token !== "string" ||
      typeof value.organizationId !== "string" ||
      !Number.isFinite(Date.parse(value.expiresAt)) ||
      Date.parse(value.expiresAt) <= Date.now()
    ) {
      clearTemporaryVeterinarySession();
      return null;
    }
    return value;
  } catch {
    clearTemporaryVeterinarySession();
    return null;
  }
}

async function temporaryRequest<T>(
  session: TemporaryVeterinarySession,
  path: string,
  method = "GET",
  body?: unknown,
  idempotencyKey?: string,
): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    method,
    credentials: "omit",
    cache: "no-store",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.token}`,
      "X-Tappet-Vet-Device": deviceId(),
      ...(idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {}),
    },
    body: body === undefined ? null : JSON.stringify(body),
  });
  const payload = (await response.json().catch(() => ({}))) as {
    data?: T;
    error?: { code: string; message: string };
  };
  if (!response.ok) {
    // A revoked temporary consent must not refresh/replace the CRM account's JWT.
    if (response.status === 401 || response.status === 403) {
      clearTemporaryVeterinarySession();
      window.dispatchEvent(new Event("tappet-temporary-vet-access-expired"));
    }
    throw new ApiError(
      response.status,
      payload.error?.code ?? "VET_ACCESS_ERROR",
      payload.error?.message ?? "No pudimos completar el acceso veterinario.",
    );
  }
  return payload.data as T;
}

export const veterinaryAccessApi = {
  createMedication: (session: TemporaryVeterinarySession, input: MedicationInput, key: string) =>
    temporaryRequest<CrmMedication>(session, "/vet/medications", "POST", input, key),
  stopMedication: (session: TemporaryVeterinarySession, id: string, input: { status: "COMPLETED" | "DISCONTINUED"; reason: string }, key: string) =>
    temporaryRequest<CrmMedication>(session, `/vet/medications/${id}/stop`, "POST", input, key),
  redeem: (organizationId: string, code: string, petPublicSlug: string) =>
    request<TemporaryVeterinaryRedeem>(
      "POST",
      `/crm/organizations/${organizationId}/vet-access/redeem`,
      { code, petPublicSlug },
      { "X-Tappet-Vet-Device": deviceId() },
    ),
  context: (
    session: TemporaryVeterinarySession,
    offsets?: { consultationsOffset?: number; vaccinationsOffset?: number },
  ) => {
    const query = new URLSearchParams({ limit: "50" });
    if (offsets?.consultationsOffset !== undefined)
      query.set("consultationsOffset", String(offsets.consultationsOffset));
    if (offsets?.vaccinationsOffset !== undefined)
      query.set("vaccinationsOffset", String(offsets.vaccinationsOffset));
    return temporaryRequest<TemporaryVeterinaryContext>(
      session,
      `/vet/context?${query.toString()}`,
    );
  },
  documents: (session: TemporaryVeterinarySession) =>
    temporaryRequest<ClinicalAttachment[]>(session, "/vet/clinical-documents"),
  consult: (session: TemporaryVeterinarySession, input: ConsultationInput, key: string) =>
    temporaryRequest<CrmConsultation>(session, "/vet/consultations", "POST", input, key),
  attachments: (session: TemporaryVeterinarySession, consultationId: string) =>
    temporaryRequest<ClinicalAttachment[]>(
      session,
      `/vet/consultations/${consultationId}/attachments`,
    ),
  attachmentIntent: (
    session: TemporaryVeterinarySession,
    consultationId: string,
    file: File,
    metadata: { category: string; title: string; documentDate: string },
  ) =>
    temporaryRequest<ClinicalUploadIntent>(
      session,
      `/vet/consultations/${consultationId}/attachments/upload-intents`,
      "POST",
      { fileName: file.name, contentType: file.type, sizeBytes: file.size, ...metadata },
      crypto.randomUUID(),
    ),
  completeAttachment: (
    session: TemporaryVeterinarySession,
    consultationId: string,
    assetId: string,
  ) =>
    temporaryRequest<unknown>(
      session,
      `/vet/consultations/${consultationId}/attachments/${assetId}/complete`,
      "POST",
      {},
    ),
};
